import * as Castle from "@castleio/castle-js";
import Cookies from "universal-cookie";

import { SessionStorage } from "site/localStorage";
import { COOKIES } from "rtr-constants";
import { logError } from "helpers/try-catch";

const SESSION_STORAGE_NAMESPACE = "devices";
const SESSION_STORAGE_KEY = "deviceUserJWT";
const CASTLE_REQUEST_TOKEN_EXPIRATION_SECONDS = 120;
export const CASTLE_REQUEST_TOKEN_REFRESH_INTERVAL_MILLISECONDS = (CASTLE_REQUEST_TOKEN_EXPIRATION_SECONDS / 2) * 1000;

const cookies = new Cookies();

const SessionStorageClient = new SessionStorage(SESSION_STORAGE_NAMESPACE);

class CastleSDK {
  #isSDKConfigured = false;
  #isMasquerading = false;

  #getIsSDKConfigured() {
    return Boolean(this.#isSDKConfigured);
  }

  setIsSDKConfigured(status) {
    if (typeof status !== "boolean") {
      const e = new Error("setIsSDKConfigured expects a boolean value");
      logError(e);
      return;
    }

    this.#isSDKConfigured = status;
  }

  #getIsMasquerading() {
    return this.#isMasquerading;
  }

  setIsMasquerading(status) {
    if (typeof status !== "boolean") {
      const e = new Error("setIsMasquerading expects a boolean value");
      logError(e);
      return;
    }

    this.#isMasquerading = status;

    if (this.#isMasquerading) {
      this.#onMasqueradeEnabled();
    }
  }

  #onMasqueradeEnabled() {
    this.deleteDeviceUserJWT();
    this.deleteCastleRequestTokenCookie();
    console.debug("Removed Castle device user JWT and request token cookies");
  }

  async setCastleRequestTokenCookie() {
    if (!this.#getIsSDKConfigured()) {
      console.debug("Skipping setting Castle SDK request token because the SDK is not configured");
      return null;
    }

    if (this.#getIsMasquerading()) {
      console.debug("Skipping setting Castle SDK request token because the user is masquerading");
      return null;
    }

    try {
      const requestToken = await Castle.createRequestToken();
      if (requestToken) {
        const date = new Date();
        date.setSeconds(date.getSeconds() + CASTLE_REQUEST_TOKEN_EXPIRATION_SECONDS);
        cookies.set(COOKIES.RTR_CASTLE_REQUEST_TOKEN, requestToken, {
          expires: date,
          path: "/",
        });
      }
      return requestToken;
    } catch (e) {
      logError(e, "Castle.createRequestToken failed");
      return null;
    }
  }

  deleteCastleRequestTokenCookie() {
    cookies.remove(COOKIES.RTR_CASTLE_REQUEST_TOKEN, { path: "/" });
  }

  #getDeviceUserJWT() {
    if (this.#getIsMasquerading()) {
      console.debug("Skipping Castle device user JWT retrieval because the user is masquerading");
      return null;
    }

    return SessionStorageClient.get(SESSION_STORAGE_KEY);
  }

  setDeviceUserJWT(user) {
    if (this.#getIsMasquerading()) {
      console.debug("Skipping Castle device user JWT setting because the user is masquerading");
      return null;
    }

    if (user?.deviceUserJWT) {
      SessionStorageClient.set(SESSION_STORAGE_KEY, user.deviceUserJWT);
    }
  }

  deleteDeviceUserJWT() {
    SessionStorageClient.remove(SESSION_STORAGE_KEY);
  }

  logPageView() {
    if (!this.#getIsSDKConfigured()) {
      console.debug("Skipping Castle page view event log because the SDK is not configured");
      return;
    }

    if (this.#getIsMasquerading()) {
      console.debug("Skipping Castle page view event log because the user is masquerading");
      return null;
    }

    const deviceUserJWT = this.#getDeviceUserJWT();
    if (deviceUserJWT) {
      Castle.page({ userJwt: deviceUserJWT });
    }
  }

  async logCustomEvent(data = {}) {
    if (!this.#getIsSDKConfigured()) {
      console.debug(`Skipping Castle ${data?.name || "custom"} event log because the SDK is not configured`);
      return;
    }

    if (this.#getIsMasquerading()) {
      console.debug(`Skipping Castle ${data?.name || "custom"} event log because the user is masquerading`);
      return;
    }

    const deviceUserJWT = this.#getDeviceUserJWT();
    if (deviceUserJWT) {
      try {
        const result = await Castle.custom({ userJwt: deviceUserJWT, ...data });
        if (!result) {
          console.error(`Castle.custom failed to save event ${data.name}`, result);
        }
      } catch (e) {
        console.error(`Castle.custom failed to save event ${data.name}`, e);
      }
    }
  }

  async loadCastleSDK(castlePk) {
    if (this.#getIsSDKConfigured()) {
      console.debug("Skipping Castle SDK initialization because it's already configured");
      return;
    }
    if (this.#getIsMasquerading()) {
      console.debug("Skipping Castle SDK initialization because the user is masquerading");
      return;
    }

    try {
      Castle.configure({ pk: castlePk });
      this.setIsSDKConfigured(true);
    } catch (e) {
      logError(e, "Castle SDK initialization failed");
      return;
    }

    await this.setCastleRequestTokenCookie();
    setInterval(() => this.setCastleRequestTokenCookie(), CASTLE_REQUEST_TOKEN_REFRESH_INTERVAL_MILLISECONDS);
    this.logPageView();
  }
}

export default new CastleSDK();
